/**
 * Discuss-store.
 *
 * Modal-scoped chat state for the "Discuss this Execution" feature, keyed
 * by the compound `(executionId, agentType)` pair. Distinct from
 * `useSessionStore` because:
 *
 *   - The Discuss modal talks to a different agent (the backend debugger,
 *     e.g. `automation_debugger`, or the app expert) than the right-hand
 *     SessionPanel.
 *   - Operators routinely have both surfaces open simultaneously (Q1 of
 *     the design validation) and the singleton SessionStore can only hold
 *     one chat at a time.
 *   - `MessageInput`/`MessageThread` reach into `useSessionStore.getState()`
 *     inside SSE callbacks, so a multi-instance refactor of those
 *     components is expensive — easier to fork a thin store and pair it
 *     with parallel `DiscussMessageThread` / `DiscussMessageInput`
 *     components that consume this one.
 *
 * Compound (executionId, agentType) keying (Task 3701h, G.1)
 * ----------------------------------------------------------
 * The Discuss modal supports switching between agents (currently
 * `'debugger'`; future `'appExpert'`) against the same approval / execution.
 * Each agent's conversation must remain isolated — switching agents must
 * NOT bleed the prior agent's transcript into the new agent's chat. State
 * is therefore nested two levels deep:
 *
 *     sessionIdByExecution: { [executionId]: { [agentType]: string } }
 *     messagesByExecution:  { [executionId]: { [agentType]: Message[] } }
 *     ... etc
 *
 * Every reducer takes both ids; the second-level record is `Partial<>`
 * because only the agents the operator has actually chatted with appear
 * in the inner record.
 *
 * Persistence: `sessionIdByExecution` is written to `localStorage` under
 * `stream.discuss` so reopening the same execution resumes the existing
 * agent-session — same persistence pattern as `sessionIdByAgent` in
 * `useSessionStore`. Messages themselves are NOT persisted; they're
 * re-fetched from the agent backend on demand via the resumed session id.
 * The persist version was bumped to 2 when compound keying landed; prior
 * v1 state is dropped on first read.
 *
 * The reducer methods mirror the slice of `useSessionStore` that's
 * actually used during streaming (text deltas, tool calls, tool results,
 * disposition / final). Memory ops and artifacts are added too for parity,
 * even though the debugger agent is unlikely to emit them — keeps the
 * SSE event handler in `DiscussMessageInput` symmetrical with the one in
 * `MessageInput` so future maintenance is easier.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  Message,
  MessagePart,
  ToolCall,
  ToolResult,
  MemoryOp,
  SessionArtifact,
  StreamingState,
} from '@/features/session/types'

// =============================================================================
// Types
// =============================================================================

/**
 * Agent currently selected in the Discuss modal. Each `(executionId,
 * agentType)` pair gets its own isolated chat slice in the store.
 *
 * - `'debugger'` — automation_debugger agent (default, currently the only
 *   shipping agent type).
 * - `'appExpert'` — app_expert agent (planned; the agent dropdown lands
 *   in a follow-up). Including the type here now keeps the store
 *   forward-compatible without a second migration.
 */
export type AgentType = 'debugger' | 'appExpert'

/** Nested-record shape for per-(executionId, agentType) state slices. */
type PerExecutionAgent<T> = Record<string, Partial<Record<AgentType, T>>>

// =============================================================================
// Ordered-parts helpers (copied verbatim from session/store.ts).
// Kept local rather than imported so the discuss store has no runtime
// dependency on the singleton session module beyond shared types.
// =============================================================================

function appendTextPart(
  parts: MessagePart[] | undefined,
  text: string,
): MessagePart[] {
  if (!parts || parts.length === 0) {
    return [{ kind: 'text', text }]
  }
  const last = parts[parts.length - 1]
  if (last.kind === 'text') {
    return [...parts.slice(0, -1), { kind: 'text', text: last.text + text }]
  }
  return [...parts, { kind: 'text', text }]
}

function pushPart(parts: MessagePart[] | undefined, part: MessagePart): MessagePart[] {
  return [...(parts ?? []), part]
}

function attachToolResult(
  parts: MessagePart[] | undefined,
  result: ToolResult,
): MessagePart[] | undefined {
  if (!parts) return parts
  return parts.map((p) =>
    p.kind === 'tool_call' && p.toolCall.id === result.toolCallId
      ? { ...p, result }
      : p,
  )
}

/**
 * Set `value` under `(executionId, agentType)` in a `PerExecutionAgent<T>`
 * map. Pure — returns a new outer record + new inner record so React /
 * zustand consumers can rely on reference equality for memoisation.
 */
function setPair<T>(
  map: PerExecutionAgent<T>,
  executionId: string,
  agentType: AgentType,
  value: T,
): PerExecutionAgent<T> {
  return {
    ...map,
    [executionId]: {
      ...(map[executionId] ?? {}),
      [agentType]: value,
    },
  }
}

/**
 * Update the inner Message[] slice for `(executionId, agentType)` by
 * applying `fn` to the current array (or to `[]` if absent). Used by
 * every message-list reducer below to avoid copy-pasted inner-shape
 * boilerplate.
 */
function updateMessages(
  map: PerExecutionAgent<Message[]>,
  executionId: string,
  agentType: AgentType,
  fn: (current: Message[]) => Message[],
): PerExecutionAgent<Message[]> {
  const current = map[executionId]?.[agentType] ?? []
  return setPair(map, executionId, agentType, fn(current))
}

/**
 * Remove the inner slice for `(executionId, agentType)` from a
 * `PerExecutionAgent<T>` map. If the resulting inner record is empty,
 * the executionId key is also removed so the outer map stays tidy.
 */
function deletePair<T>(
  map: PerExecutionAgent<T>,
  executionId: string,
  agentType: AgentType,
): PerExecutionAgent<T> {
  const inner = map[executionId]
  if (!inner) return map
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [agentType]: _drop, ...innerRest } = inner
  const restKeys = Object.keys(innerRest)
  const { [executionId]: _outerDrop, ...outerRest } = map
  if (restKeys.length === 0) {
    return outerRest
  }
  return { ...outerRest, [executionId]: innerRest as Partial<Record<AgentType, T>> }
}

// =============================================================================
// State + actions
// =============================================================================

interface DiscussStoreState {
  /**
   * Backend agent session id per (execution, agentType). Persisted so
   * reopening the Discuss modal for the same (execution, agent) pair
   * resumes the conversation rather than starting a fresh thread the
   * operator would have to scroll back through. Generated via
   * `generateSessionId()` on first send.
   */
  sessionIdByExecution: PerExecutionAgent<string>

  // In-memory chat state, keyed by (executionId, agentType).
  messagesByExecution: PerExecutionAgent<Message[]>
  streamingStateByExecution: PerExecutionAgent<StreamingState>
  errorByExecution: PerExecutionAgent<string | null>
  /**
   * Whether the markdown context envelope has been physically prepended
   * to a user turn for this (execution, agentType) slice. Flip flag
   * governs the `<execution_context>` envelope: true once the operator's
   * first send carried it, so subsequent turns ship bare messages.
   */
  contextSentByExecution: PerExecutionAgent<boolean>
}

interface DiscussStoreActions {
  // Session id management
  getSessionId: (executionId: string, agentType: AgentType) => string | undefined
  setSessionIdForExecution: (
    executionId: string,
    agentType: AgentType,
    sessionId: string,
  ) => void

  // Message reducer (mirrors useSessionStore)
  addMessage: (
    executionId: string,
    agentType: AgentType,
    message: Message,
  ) => void
  updateMessage: (
    executionId: string,
    agentType: AgentType,
    id: string,
    updates: Partial<Message>,
  ) => void
  appendStreamingContent: (
    executionId: string,
    agentType: AgentType,
    messageId: string,
    content: string,
  ) => void
  addToolCall: (
    executionId: string,
    agentType: AgentType,
    messageId: string,
    toolCall: ToolCall,
  ) => void
  addToolResult: (
    executionId: string,
    agentType: AgentType,
    messageId: string,
    result: ToolResult,
  ) => void
  addMemoryOp: (
    executionId: string,
    agentType: AgentType,
    messageId: string,
    memOp: MemoryOp,
  ) => void
  addArtifact: (
    executionId: string,
    agentType: AgentType,
    messageId: string,
    artifact: SessionArtifact,
  ) => void

  // Streaming state
  setStreamingState: (
    executionId: string,
    agentType: AgentType,
    state: StreamingState,
  ) => void
  setError: (
    executionId: string,
    agentType: AgentType,
    message: string | null,
  ) => void

  // Context-sent flag
  markContextSent: (executionId: string, agentType: AgentType) => void
  /** Restart-conversation helper: flip contextSent back to false so the
   * next message re-prepends the envelope. */
  resetContextSent: (executionId: string, agentType: AgentType) => void

  /** Wipe messages for one (executionId, agentType) slice. The persisted
   * sessionId is left intact so callers can decide whether to mint a
   * fresh one separately (the Restart-conversation flow does). */
  clearMessages: (executionId: string, agentType: AgentType) => void

  /** Wipe in-memory chat for one (execution, agentType) slice. Persisted
   * sessionId survives. Leaves other agents' slices for the same
   * execution untouched. */
  resetExecution: (executionId: string, agentType: AgentType) => void
  /** Hard-reset including the persisted sessionId — used when the operator
   * deliberately wants a brand-new debugging conversation. Per-agent so
   * the other agent's persisted session is preserved. */
  forgetExecution: (executionId: string, agentType: AgentType) => void
}

export const useDiscussStore = create<DiscussStoreState & DiscussStoreActions>()(
  persist(
    (set, get) => ({
      sessionIdByExecution: {},
      messagesByExecution: {},
      streamingStateByExecution: {},
      errorByExecution: {},
      contextSentByExecution: {},

      getSessionId: (executionId, agentType) =>
        get().sessionIdByExecution[executionId]?.[agentType],

      setSessionIdForExecution: (executionId, agentType, sessionId) =>
        set((s) => ({
          sessionIdByExecution: setPair(
            s.sessionIdByExecution,
            executionId,
            agentType,
            sessionId,
          ),
        })),

      addMessage: (executionId, agentType, message) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) => [...current, message],
          ),
        })),

      updateMessage: (executionId, agentType, id, updates) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) => (m.id === id ? { ...m, ...updates } : m)),
          ),
        })),

      appendStreamingContent: (executionId, agentType, messageId, content) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      content: m.content + content,
                      parts: appendTextPart(m.parts, content),
                    }
                  : m,
              ),
          ),
        })),

      addToolCall: (executionId, agentType, messageId, toolCall) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      toolCalls: [...(m.toolCalls ?? []), toolCall],
                      parts: pushPart(m.parts, { kind: 'tool_call', toolCall }),
                    }
                  : m,
              ),
          ),
        })),

      addToolResult: (executionId, agentType, messageId, result) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      toolResults: [...(m.toolResults ?? []), result],
                      parts: attachToolResult(m.parts, result),
                    }
                  : m,
              ),
          ),
        })),

      addMemoryOp: (executionId, agentType, messageId, memOp) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      memoryOps: [...(m.memoryOps ?? []), memOp],
                      parts: pushPart(m.parts, { kind: 'memory_op', memoryOp: memOp }),
                    }
                  : m,
              ),
          ),
        })),

      addArtifact: (executionId, agentType, messageId, artifact) =>
        set((s) => ({
          messagesByExecution: updateMessages(
            s.messagesByExecution,
            executionId,
            agentType,
            (current) =>
              current.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      artifacts: [...(m.artifacts ?? []), artifact],
                      parts: pushPart(m.parts, { kind: 'artifact', artifact }),
                    }
                  : m,
              ),
          ),
        })),

      setStreamingState: (executionId, agentType, state) =>
        set((s) => ({
          streamingStateByExecution: setPair(
            s.streamingStateByExecution,
            executionId,
            agentType,
            state,
          ),
        })),

      setError: (executionId, agentType, message) =>
        set((s) => ({
          errorByExecution: setPair(
            s.errorByExecution,
            executionId,
            agentType,
            message,
          ),
        })),

      markContextSent: (executionId, agentType) =>
        set((s) => ({
          contextSentByExecution: setPair(
            s.contextSentByExecution,
            executionId,
            agentType,
            true,
          ),
        })),

      resetContextSent: (executionId, agentType) =>
        set((s) => ({
          contextSentByExecution: setPair(
            s.contextSentByExecution,
            executionId,
            agentType,
            false,
          ),
        })),

      clearMessages: (executionId, agentType) =>
        set((s) => ({
          messagesByExecution: setPair(
            s.messagesByExecution,
            executionId,
            agentType,
            [],
          ),
        })),

      resetExecution: (executionId, agentType) =>
        set((s) => ({
          messagesByExecution: deletePair(s.messagesByExecution, executionId, agentType),
          streamingStateByExecution: deletePair(
            s.streamingStateByExecution,
            executionId,
            agentType,
          ),
          errorByExecution: deletePair(s.errorByExecution, executionId, agentType),
          contextSentByExecution: deletePair(
            s.contextSentByExecution,
            executionId,
            agentType,
          ),
        })),

      forgetExecution: (executionId, agentType) =>
        set((s) => ({
          sessionIdByExecution: deletePair(
            s.sessionIdByExecution,
            executionId,
            agentType,
          ),
          messagesByExecution: deletePair(s.messagesByExecution, executionId, agentType),
          streamingStateByExecution: deletePair(
            s.streamingStateByExecution,
            executionId,
            agentType,
          ),
          errorByExecution: deletePair(s.errorByExecution, executionId, agentType),
          contextSentByExecution: deletePair(
            s.contextSentByExecution,
            executionId,
            agentType,
          ),
        })),
    }),
    {
      name: 'stream.discuss',
      storage: createJSONStorage(() => localStorage),
      // Only the session id map needs to survive reloads. The chat
      // transcript itself is reloaded from the agents backend on resume,
      // so persisting messages would just create reconciliation pain.
      partialize: (state) => ({
        sessionIdByExecution: state.sessionIdByExecution,
      }),
      // v2 (Task 3701h): nested compound (executionId, agentType) shape.
      // Bumping the version invalidates any persisted v1 flat
      // `Record<string, string>` state — Zustand drops it on first read,
      // which is acceptable because the only persisted slice was
      // sessionIds (chat itself was never persisted) and stale sessionIds
      // just mean the next message mints a fresh agent session.
      version: 2,
    },
  ),
)

// =============================================================================
// Selectors
// =============================================================================

/**
 * Stable empty-array reference returned by `selectMessages` when a slice
 * has no chat yet. MUST be a module-level constant, not a fresh `[]`
 * literal: `selectMessages` is used as a live zustand v5 subscription in
 * `DiscussMessageThread`, and zustand compares selector output with
 * `Object.is`. Returning a new array each call makes the store look
 * perpetually changed and re-renders the thread without end —
 * "Maximum update depth exceeded" (React #185), triggered the instant
 * the Discuss modal opens before any messages have loaded.
 */
const EMPTY_MESSAGES: readonly Message[] = []

/**
 * Read the messages list for an (execution, agentType) slice. Returns
 * an empty array when the slice has no chat yet (rather than
 * `undefined`) so callers can pass the result straight into a thread
 * renderer without null-guarding.
 */
export function selectMessages(
  state: DiscussStoreState,
  executionId: string | null,
  agentType: AgentType,
): Message[] {
  if (!executionId) return EMPTY_MESSAGES as Message[]
  return state.messagesByExecution[executionId]?.[agentType] ?? (EMPTY_MESSAGES as Message[])
}

export function selectStreamingState(
  state: DiscussStoreState,
  executionId: string | null,
  agentType: AgentType,
): StreamingState {
  if (!executionId) return 'idle'
  return state.streamingStateByExecution[executionId]?.[agentType] ?? 'idle'
}

export function selectContextSent(
  state: DiscussStoreState,
  executionId: string | null,
  agentType: AgentType,
): boolean {
  if (!executionId) return false
  return state.contextSentByExecution[executionId]?.[agentType] ?? false
}

/** Count messages for the given (execution, agentType) slice. */
export function selectMessageCount(
  state: DiscussStoreState,
  executionId: string | null,
  agentType: AgentType,
): number {
  if (!executionId) return 0
  return state.messagesByExecution[executionId]?.[agentType]?.length ?? 0
}
